import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Recipe } from 'public-contracts';
import { recipesService } from '../services/recipesService';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { ChefHat, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../utils';

interface RecipeComboboxProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowNone?: boolean;
  noneLabel?: string;
  excludeRecipeId?: string; // Exclude a recipe from the list (e.g., to prevent circular references)
  'data-testid'?: string;
}

export const RecipeCombobox: React.FC<RecipeComboboxProps> = ({
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  allowNone = false,
  noneLabel,
  excludeRecipeId,
  'data-testid': dataTestId,
}) => {
  const { translate } = useTranslation();
  const [open, setOpen] = useState(false);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const loadRecipes = React.useCallback(async () => {
    try {
      setIsLoading(true);
      // Load all recipes - Command component will handle client-side filtering
      const data = await recipesService.getRecipes({
        page: 1,
        size: 1000, // Fetch all recipes
      });
      const recipesList = Array.isArray(data) ? data : [];
      // Filter out excluded recipe if provided
      const filteredRecipes = excludeRecipeId
        ? recipesList.filter((recipe) => recipe.id !== excludeRecipeId)
        : recipesList;
      setRecipes(filteredRecipes);
    } catch (err) {
      console.error('Failed to load recipes:', err);
      setRecipes([]);
    } finally {
      setIsLoading(false);
    }
  }, [excludeRecipeId]);

  // Load all recipes when popover opens (only once)
  useEffect(() => {
    if (open && recipes.length === 0) {
      loadRecipes();
    }
  }, [open, recipes.length, loadRecipes]);

  // Auto-focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const selectedRecipe = recipes.find((recipe) => recipe.id === value);

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === '__none__') {
      onValueChange('');
    } else {
      onValueChange(selectedValue);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
          data-testid={dataTestId}
        >
          <span className="flex items-center gap-2 truncate">
            {value && selectedRecipe ? (
              <>
                <ChefHat className="h-4 w-4 shrink-0" />
                <span className="truncate">{selectedRecipe.name}</span>
              </>
            ) : allowNone && !value ? (
              <span className="text-muted-foreground">
                {noneLabel ||
                  translate(
                    'pos.ingredients.recipe.none',
                    'No recipe'
                  )}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {placeholder ||
                  translate(
                    'pos.ingredients.placeholders.selectRecipe',
                    'Select recipe'
                  )}
              </span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[400px] p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 0);
        }}
      >
        <Command>
          <CommandInput
            ref={searchInputRef}
            placeholder={translate(
              'pos.ingredients.placeholders.searchRecipe',
              'Search recipes...'
            )}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? translate('common.loading', 'Loading...')
                : translate('common.noResults', 'No recipes found.')}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value="__none__"
                  onSelect={() => handleSelect('__none__')}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="text-muted-foreground">
                    {noneLabel ||
                      translate(
                        'pos.ingredients.recipe.none',
                        'No recipe'
                      )}
                  </span>
                </CommandItem>
              )}
              {recipes.map((recipe) => (
                <CommandItem
                  key={recipe.id}
                  value={recipe.name}
                  onSelect={() => handleSelect(recipe.id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === recipe.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center space-x-2">
                    <ChefHat className="h-4 w-4" />
                    <span>{recipe.name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

